import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Envelope, CheckCircle } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [mode, setMode] = useState('reset'); // 'reset' | 'verify'

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const endpoint = mode === 'reset' ? 'forgot-password' : 'resend-verification';
      await axios.post(`${API}/api/auth/${endpoint}`, { email });
      setSent(true);
    } catch (err) {
      // Always show success to prevent email enumeration
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-10 text-center shadow-sm" data-testid="forgot-success">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-950/30 mb-6">
            <CheckCircle size={40} weight="duotone" className="text-green-600" />
          </div>
          <h2 className="text-3xl mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
            Check your inbox
          </h2>
          <p className="text-muted-foreground text-sm mb-6">
            If an account exists for <b className="text-foreground">{email}</b>, we&apos;ve sent a {mode === 'reset' ? 'password reset' : 'new verification'} link.
          </p>
          <p className="text-xs text-muted-foreground mb-6">
            The link is valid for {mode === 'reset' ? '1 hour' : '24 hours'}.
          </p>
          <Link to="/login" data-testid="forgot-back-login" className="inline-block bg-primary text-primary-foreground rounded-full px-6 py-3 font-medium hover:opacity-90">
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-lg p-10 shadow-sm">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Envelope size={32} weight="duotone" className="text-primary" />
            </div>
            <h1 className="text-3xl md:text-4xl tracking-tight mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
              {mode === 'reset' ? 'Reset password' : 'Resend verification'}
            </h1>
            <p className="text-muted-foreground text-sm">
              {mode === 'reset'
                ? 'Enter your email and we\'ll send a reset link.'
                : 'We\'ll resend your email verification link.'}
            </p>
          </div>

          <div className="flex gap-2 mb-6 p-1 bg-muted/50 rounded-full">
            <button
              type="button"
              onClick={() => setMode('reset')}
              data-testid="forgot-tab-reset"
              className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors ${
                mode === 'reset' ? 'bg-background shadow-sm' : 'text-muted-foreground'
              }`}
            >
              Reset password
            </button>
            <button
              type="button"
              onClick={() => setMode('verify')}
              data-testid="forgot-tab-verify"
              className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors ${
                mode === 'verify' ? 'bg-background shadow-sm' : 'text-muted-foreground'
              }`}
            >
              Resend verification
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email-input"
                placeholder="you@example.com"
                className="w-full px-3 py-3 text-sm bg-muted/40 border border-transparent focus:border-primary focus:bg-background rounded-lg outline-none"
              />
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="forgot-submit"
              className="w-full bg-primary text-primary-foreground rounded-full py-3 font-semibold hover:opacity-90 disabled:opacity-60"
            >
              {busy ? 'Sending…' : 'Send link'}
            </button>

            <p className="text-center text-sm text-muted-foreground pt-2">
              Remembered it?{' '}
              <Link to="/login" className="text-primary font-medium hover:underline">Sign in</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
